import React, { useEffect, useState } from "react";
import { useSearchParams, useParams, Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { resolveUrl } from "@/components/ImageUploader";
import StatusBadge from "@/components/StatusBadge";
import { Search, ArrowLeft, X } from "lucide-react";

export default function StorefrontSearch() {
  const { slug } = useParams();
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const q = params.get("q") || "";
  const categoria = params.get("categoria") || "";
  const colore = params.get("colore") || "";
  const materiale = params.get("materiale") || "";
  const priceMin = params.get("price_min") || "";
  const priceMax = params.get("price_max") || "";

  useEffect(() => {
    axios.get(`${API}/public/storefront/${slug}`).then((r) => {
      setTenant(r.data.tenant);
      setStatuses(r.data.network.vertical_config?.availability_statuses || []);
    });
  }, [slug]);

  useEffect(() => {
    const sp = new URLSearchParams({ tenant_slug: slug });
    if (q) sp.set("q", q);
    if (categoria) sp.set("categoria", categoria);
    if (colore) sp.set("colore", colore);
    if (materiale) sp.set("materiale", materiale);
    if (priceMin) sp.set("price_min", priceMin);
    if (priceMax) sp.set("price_max", priceMax);
    axios.get(`${API}/public/search?${sp.toString()}`).then((r) => setData(r.data));
  }, [slug, q, categoria, colore, materiale, priceMin, priceMax]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  const clearAll = () => setParams(new URLSearchParams());
  const facets = data?.facets || { colors: [], materials: [], categories: [] };

  return (
    <div className="min-h-screen bg-white" data-testid="storefront-search">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to={`/s/${slug}`} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="w-4 h-4" /> {tenant?.name}</Link>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-10">
        <div className="mb-8">
          <h1 className="editorial-h1 text-4xl mb-3">Cerca nel catalogo</h1>
          <div className="relative max-w-2xl">
            <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input data-testid="search-input" value={q} onChange={(e) => setParam("q", e.target.value)}
              placeholder="Cerca per nome, descrizione, materiale, colore..."
              className="w-full h-14 pl-12 pr-12 rounded-sm border border-border focus:border-brand focus:ring-1 focus:ring-brand outline-none text-base" />
            {q && <button data-testid="search-clear" onClick={() => setParam("q", "")} className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full hover:bg-accent flex items-center justify-center"><X className="w-4 h-4" /></button>}
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-8">
          <aside className="lg:col-span-3 space-y-5 self-start lg:sticky lg:top-24">
            {(categoria || colore || materiale || priceMin || priceMax) && (
              <button data-testid="filters-clear" onClick={clearAll} className="text-xs text-brand hover:underline">Pulisci filtri</button>
            )}

            <Facet title="Categoria" testid="facet-categoria" options={facets.categories} value={categoria} onChange={(v) => setParam("categoria", v)} />
            <Facet title="Colore" testid="facet-colore" options={facets.colors} value={colore} onChange={(v) => setParam("colore", v)} />
            <Facet title="Materiale" testid="facet-materiale" options={facets.materials} value={materiale} onChange={(v) => setParam("materiale", v)} />

            <div>
              <div className="overline text-muted-foreground mb-2">Prezzo (€)</div>
              <div className="flex gap-2">
                <input data-testid="search-price-min" type="number" placeholder="min" value={priceMin} onChange={(e) => setParam("price_min", e.target.value)} className="w-full h-9 px-2 rounded-sm border border-border text-sm" />
                <input data-testid="search-price-max" type="number" placeholder="max" value={priceMax} onChange={(e) => setParam("price_max", e.target.value)} className="w-full h-9 px-2 rounded-sm border border-border text-sm" />
              </div>
            </div>
          </aside>

          <div className="lg:col-span-9">
            <div className="text-sm text-muted-foreground mb-4">{data?.count ?? 0} risultati</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {(data?.results || []).map((it) => (
                <Link key={it.sku_variante} to={`/s/${it.tenant_slug}/product/${it.sku_variante}`} data-testid={`search-result-${it.sku_variante}`} className="group block">
                  <div className="aspect-[4/5] bg-neutral-100 mb-3 overflow-hidden relative">
                    {it.immagini?.[0] && <img src={resolveUrl(it.immagini[0])} alt={it.nome} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />}
                    <div className="absolute top-3 left-3"><StatusBadge status={it.stato_disponibilita} statuses={statuses} /></div>
                  </div>
                  <div className="overline text-muted-foreground mb-1 capitalize">{it.categoria}</div>
                  <h3 className="font-heading font-bold text-lg leading-tight">{it.nome}</h3>
                  <div className="text-xs text-muted-foreground">{Object.values(it.custom_attributes || {}).slice(0, 3).join(" · ")}</div>
                  <div className="flex items-baseline gap-2 mt-1.5">
                    <span className="font-heading font-bold">€ {it.prezzo_finale}</span>
                    {it.sconto_percentuale > 0 && <span className="text-xs text-muted-foreground line-through">€ {it.prezzo_originale}</span>}
                  </div>
                </Link>
              ))}
            </div>
            {data && data.results.length === 0 && (
              <div className="text-center py-20 border border-dashed border-border text-sm text-muted-foreground">
                Nessun risultato. Prova a modificare i filtri.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const Facet = ({ title, options, value, onChange, testid }) => (
  <div data-testid={testid}>
    <div className="overline text-muted-foreground mb-2">{title}</div>
    <div className="space-y-1 max-h-48 overflow-y-auto">
      <button onClick={() => onChange("")} className={`block w-full text-left px-2 py-1 rounded-sm text-sm ${!value ? "bg-foreground text-white" : "hover:bg-accent"}`}>Tutti</button>
      {options.map((o) => (
        <button key={o} data-testid={`${testid}-${o}`} onClick={() => onChange(o)}
          className={`block w-full text-left px-2 py-1 rounded-sm text-sm capitalize ${value === o ? "bg-foreground text-white" : "hover:bg-accent"}`}>
          {o}
        </button>
      ))}
    </div>
  </div>
);
